/**
 * Admin Agent Chat Component
 *
 * Full-page chat interface for the admin agent. Handles message display,
 * streaming, tool call indicators, and session management.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import { BlockNoteView } from '@blocknote/mantine';
import { useCreateBlockNote } from '@blocknote/react';
import { EmptyState, InputFooter, ScrollContainer } from '@components/ui/chat';
import { generateRunId } from '@features/ai-chat-core';
import { useMessageHover } from '@hooks/useMessageHover';
import {
  ArrowUpward as SendIcon,
  Article as PayloadIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Delete as ClearIcon,
  PictureAsPdf as PdfIcon,
  SmartToy as AgentIcon,
} from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import {
  addUserMessage,
  type AgentChatState,
  type AgentToolCall,
  clearError,
  closeMarkdownPanel,
  type MarkdownPayload,
  newConversation,
  setActivePayloadIndex,
} from '../store/agentChatSlice';
import { useExportAgentPdfMutation } from '../store/api';
import { useStreamAgentChatQuery } from '../store/agentStreamingApi';

import '@blocknote/core/fonts/inter.css';
import '@blocknote/mantine/style.css';

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Tool call activity indicator - shows when agent is calling tools
 */
const ToolCallBubble: React.FC<{ toolCall: AgentToolCall }> = React.memo(({ toolCall }) => {
  const theme = useTheme();

  // Format tool name for display (remove mcp__ prefix, replace underscores)
  const displayName = toolCall.toolCallName
    .replace(/^mcp__\w+__/, '')
    .replace(/_/g, ' ');

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 1,
        animation: 'fadeIn 0.3s ease-in',
        '@keyframes fadeIn': {
          '0%': { opacity: 0, transform: 'translateY(10px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Avatar
        sx={{
          width: 28,
          height: 28,
          bgcolor: 'transparent',
          background: theme.palette.chat?.gradients?.ai || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <AgentIcon sx={{ fontSize: 16, color: 'white' }} />
      </Avatar>
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          maxWidth: '75%',
          bgcolor: 'transparent',
          border: `1px dashed ${theme.palette.divider}`,
          borderRadius: 2,
          borderTopLeftRadius: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '0.875rem',
            fontStyle: 'italic',
          }}
        >
          Calling {displayName}...
        </Typography>
        <CircularProgress size={14} thickness={4} sx={{ color: theme.palette.text.secondary }} />
      </Paper>
    </Box>
  );
});
ToolCallBubble.displayName = 'ToolCallBubble';

/**
 * Thinking indicator bubble
 */
const ThinkingBubble: React.FC<{ message?: string }> = React.memo(({ message = 'Thinking...' }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 1,
        animation: 'fadeIn 0.3s ease-in',
        '@keyframes fadeIn': {
          '0%': { opacity: 0, transform: 'translateY(10px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Avatar
        sx={{
          width: 28,
          height: 28,
          bgcolor: 'transparent',
          background: theme.palette.chat?.gradients?.ai || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <AgentIcon sx={{ fontSize: 16, color: 'white' }} />
      </Avatar>
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          maxWidth: '75%',
          bgcolor: 'transparent',
          border: `1px dashed ${theme.palette.divider}`,
          borderRadius: 2,
          borderTopLeftRadius: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '0.875rem',
            fontStyle: 'italic',
          }}
        >
          {message}
        </Typography>
        <CircularProgress size={14} thickness={4} sx={{ color: theme.palette.text.secondary }} />
      </Paper>
    </Box>
  );
});
ThinkingBubble.displayName = 'ThinkingBubble';

/**
 * Message bubble with markdown rendering for assistant messages
 */
const AgentMessageBubble: React.FC<{
  id: string;
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
  isHovered?: boolean;
  onMouseEnter?: (id: string) => void;
  onMouseLeave?: () => void;
}> = React.memo(({ id, role, content, isStreaming, onMouseEnter, onMouseLeave }) => {
  const theme = useTheme();
  const isUser = role === 'user';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        alignItems: 'flex-start',
        gap: 1,
        animation: 'fadeIn 0.3s ease-in',
      }}
      onMouseEnter={() => onMouseEnter?.(id)}
      onMouseLeave={() => onMouseLeave?.()}
    >
      {/* Avatar for assistant */}
      {!isUser && (
        <Avatar
          sx={{
            width: 28,
            height: 28,
            bgcolor: 'transparent',
            background: theme.palette.chat?.gradients?.ai || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          }}
        >
          <AgentIcon sx={{ fontSize: 16, color: 'white' }} />
        </Avatar>
      )}

      {/* Message bubble */}
      <Paper
        elevation={isUser ? 2 : 1}
        sx={{
          p: 1.5,
          maxWidth: '80%',
          bgcolor: isUser
            ? (theme.palette.chat?.colors?.userBubble || theme.palette.primary.main)
            : (theme.palette.chat?.colors?.messageBubbleBackground || '#f5f5f5'),
          color: isUser
            ? (theme.palette.chat?.colors?.userText || '#fff')
            : (theme.palette.chat?.colors?.assistantText || theme.palette.text.primary),
          borderRadius: 2,
          borderTopRightRadius: isUser ? 0 : 16,
          borderTopLeftRadius: isUser ? 16 : 0,
          boxShadow: isUser ? '0 2px 4px rgba(0,0,0,0.1)' : '0 1px 3px rgba(0,0,0,0.08)',
          border: isUser
            ? 'none'
            : `1px solid ${theme.palette.chat?.colors?.messageBubbleBorder || theme.palette.divider}`,
        }}
      >
        {/* Role label */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              color: isUser ? 'rgba(255,255,255,0.7)' : theme.palette.text.secondary,
              fontWeight: 500,
              fontSize: 12,
            }}
          >
            {isUser ? 'You' : 'Agent'}
          </Typography>
          {isStreaming && (
            <CircularProgress
              size={10}
              thickness={5}
              sx={{ ml: 1, color: theme.palette.text.secondary }}
            />
          )}
        </Box>

        {/* Content - markdown for assistant, plain text for user */}
        {isUser ? (
          <Typography variant="body2" sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {content}
          </Typography>
        ) : (
          <Box
            sx={{
              '& p': {
                fontSize: '0.875rem',
                lineHeight: 1.6,
                m: '0.25em 0',
                '&:first-of-type': { mt: 0 },
                '&:last-of-type': { mb: 0 },
              },
              '& h1, & h2, & h3': {
                fontSize: '0.9375rem',
                fontWeight: 600,
                lineHeight: 1.3,
                m: '0.75em 0 0.25em',
              },
              '& strong': { fontWeight: 600 },
              '& code': {
                fontSize: '0.8125rem',
                fontFamily: 'monospace',
                bgcolor: 'rgba(0,0,0,0.05)',
                p: '0.125em 0.375em',
                borderRadius: '3px',
              },
              '& pre': {
                fontSize: '0.8125rem',
                fontFamily: 'monospace',
                bgcolor: 'rgba(0,0,0,0.05)',
                p: 1.5,
                borderRadius: 1,
                overflow: 'auto',
                my: 1,
              },
              '& ul, & ol': {
                fontSize: '0.875rem',
                lineHeight: 1.6,
                pl: 2.5,
                my: 0.5,
              },
              '& li': {
                fontSize: '0.875rem',
                lineHeight: 1.6,
                my: 0.25,
              },
              '& table': {
                fontSize: '0.8125rem',
                borderCollapse: 'collapse',
                width: '100%',
                my: 1,
              },
              '& th, & td': {
                border: `1px solid ${theme.palette.divider}`,
                p: '4px 8px',
                textAlign: 'left',
              },
              '& th': {
                bgcolor: 'rgba(0,0,0,0.03)',
                fontWeight: 600,
              },
              '& blockquote': {
                borderLeft: `3px solid ${theme.palette.divider}`,
                pl: 1.5,
                ml: 0,
                color: theme.palette.text.secondary,
                fontStyle: 'italic',
              },
            }}
          >
            <Markdown>{content}</Markdown>
          </Box>
        )}
      </Paper>

      {/* Avatar for user */}
      {isUser && (
        <Avatar
          sx={{
            width: 28,
            height: 28,
            bgcolor: theme.palette.primary.main,
            fontSize: 14,
          }}
        >
          U
        </Avatar>
      )}
    </Box>
  );
});
AgentMessageBubble.displayName = 'AgentMessageBubble';

/**
 * Clickable chip in the chat flow indicating a markdown payload was received.
 * Clicking it opens the payload in the side panel.
 */
const MarkdownPayloadChip: React.FC<{
  payload: MarkdownPayload;
  index: number;
  isActive: boolean;
  onClick: (index: number) => void;
}> = React.memo(({ payload, index, isActive, onClick }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 1,
        animation: 'fadeIn 0.3s ease-in',
        '@keyframes fadeIn': {
          '0%': { opacity: 0, transform: 'translateY(10px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Avatar
        sx={{
          width: 28,
          height: 28,
          bgcolor: 'transparent',
          background:
            theme.palette.chat?.gradients?.ai ||
            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <AgentIcon sx={{ fontSize: 16, color: 'white' }} />
      </Avatar>
      <Paper
        elevation={0}
        onClick={() => onClick(index)}
        sx={{
          p: 1.5,
          maxWidth: '75%',
          bgcolor: isActive ? 'rgba(77, 50, 215, 0.08)' : 'transparent',
          border: `1px solid ${isActive ? '#4D32D7' : theme.palette.divider}`,
          borderRadius: 2,
          borderTopLeftRadius: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: 'rgba(77, 50, 215, 0.05)',
            borderColor: '#4D32D7',
          },
        }}
      >
        <PayloadIcon sx={{ fontSize: 18, color: '#4D32D7' }} />
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.primary,
            fontSize: '0.875rem',
            fontWeight: 500,
          }}
        >
          {payload.title}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '0.75rem',
            ml: 'auto',
          }}
        >
          View
        </Typography>
      </Paper>
    </Box>
  );
});
MarkdownPayloadChip.displayName = 'MarkdownPayloadChip';

/**
 * BlockNote viewer that renders markdown without the normalizeMarkdownNewlines pipeline.
 * The shared EditorView runs normalizations that break table formatting by inserting
 * blank lines between table rows. This component uses BlockNote directly with the
 * full default schema (including code blocks and tables).
 */
const RawBlockNoteViewer: React.FC<{ markdown: string }> = React.memo(({ markdown }) => {
  const editor = useCreateBlockNote();
  const [isReady, setIsReady] = useState(false);
  const lastMarkdownRef = useRef('');

  // Initialize editor with markdown content (no normalization)
  useEffect(() => {
    const init = async () => {
      try {
        if (markdown) {
          const blocks = await editor.tryParseMarkdownToBlocks(markdown);
          const validBlocks = blocks.filter(block => block != null);
          if (validBlocks.length > 0) {
            editor.replaceBlocks(editor.document, validBlocks);
          }
        }
        lastMarkdownRef.current = markdown;
        setIsReady(true);
      } catch (err) {
        console.error('Error initializing BlockNote viewer:', err);
        setIsReady(true);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  // Update content when markdown changes (e.g. switching between payloads)
  useEffect(() => {
    if (!isReady || lastMarkdownRef.current === markdown) return;

    const update = async () => {
      try {
        if (markdown) {
          const blocks = await editor.tryParseMarkdownToBlocks(markdown);
          const validBlocks = blocks.filter(block => block != null);
          if (validBlocks.length > 0) {
            editor.replaceBlocks(editor.document, validBlocks);
          }
        }
        lastMarkdownRef.current = markdown;
      } catch (err) {
        console.error('Error updating BlockNote viewer:', err);
      }
    };
    update();
  }, [editor, markdown, isReady]);

  if (!isReady) return null;

  return (
    <BlockNoteView
      editor={editor}
      editable={false}
      theme="light"
      sideMenu={false}
      formattingToolbar={false}
      style={{ padding: 0, margin: 0 }}
    />
  );
});
RawBlockNoteViewer.displayName = 'RawBlockNoteViewer';

/**
 * Resizable side panel for displaying markdown payloads.
 * Includes a drag handle for resizing and is independently scrollable.
 */
const MarkdownPanel: React.FC<{
  payload: MarkdownPayload;
  panelWidth: number;
  onClose: () => void;
  onDragStart: (e: React.MouseEvent) => void;
}> = React.memo(({ payload, panelWidth, onClose, onDragStart }) => {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);
  const [exportPdf, { isLoading: isPdfLoading }] = useExportAgentPdfMutation();

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(payload.markdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy markdown:', err);
    }
  }, [payload.markdown]);

  const handleDownloadPdf = useCallback(async () => {
    try {
      const blob = await exportPdf({
        title: payload.title,
        markdown: payload.markdown,
      }).unwrap();

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${payload.title.replace(/[^a-zA-Z0-9_-]/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export PDF:', err);
    }
  }, [exportPdf, payload.title, payload.markdown]);

  return (
    <Box
      sx={{
        width: panelWidth,
        minWidth: 300,
        maxWidth: '80vw',
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
        flexShrink: 0,
      }}
    >
      {/* Drag handle */}
      <Box
        onMouseDown={onDragStart}
        sx={{
          width: 6,
          cursor: 'col-resize',
          bgcolor: 'transparent',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-color 0.15s ease',
          '&:hover': {
            bgcolor: 'rgba(77, 50, 215, 0.15)',
          },
          '&::after': {
            content: '""',
            display: 'block',
            width: 2,
            height: 40,
            borderRadius: 1,
            bgcolor: theme.palette.divider,
          },
        }}
      />

      {/* Panel content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          borderLeft: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
          overflow: 'hidden',
        }}
      >
        {/* Panel header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <PayloadIcon sx={{ fontSize: 20, color: '#4D32D7', flexShrink: 0 }} />
            <Typography
              variant="subtitle1"
              noWrap
              sx={{fontWeight: 600,  overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {payload.title}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={copied ? 'Copied!' : 'Copy markdown'}>
              <IconButton onClick={handleCopy} size="small" color={copied ? 'success' : 'default'}>
                {copied ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Download PDF">
              <IconButton onClick={handleDownloadPdf} size="small" disabled={isPdfLoading}>
                {isPdfLoading ? (
                  <CircularProgress size={18} />
                ) : (
                  <PdfIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="Close panel">
              <IconButton onClick={onClose} size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Scrollable BlockNote content (read-only, no normalization) */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            px: 1,
            py: 1,
          }}
        >
          <RawBlockNoteViewer markdown={payload.markdown} />
        </Box>
      </Box>
    </Box>
  );
});
MarkdownPanel.displayName = 'MarkdownPanel';

// ============================================================================
// Stable empty defaults (avoid new references on each render)
// ============================================================================

const emptyMessages: AgentChatState['messages'] = [];
const emptyActivities: AgentChatState['activities'] = {};
const emptyToolCalls: AgentChatState['activeToolCalls'] = [];
const emptyPayloads: AgentChatState['markdownPayloads'] = [];

const MIN_PANEL_WIDTH = 300;
const DEFAULT_PANEL_WIDTH = 600;

// ============================================================================
// Main Component
// ============================================================================

export function AgentChat() {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  // Local state
  const [input, setInput] = useState('');
  const [activeStream, setActiveStream] = useState<{
    prompt: string;
    runId: string;
  } | null>(null);
  const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH);
  const isDraggingRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { hoveredMessageId, handleMouseEnter, handleMouseLeave } = useMessageHover();

  // Redux state - dynamic reducer accessed via type assertion (standard pattern for dynamic features)
  const chatState = useAppSelector(
    state =>
      (state as unknown as Record<string, AgentChatState>)['adminAgent/chat'] as
        | AgentChatState
        | undefined,
  );

  const messages = chatState?.messages ?? emptyMessages;
  const activities = chatState?.activities ?? emptyActivities;
  const assistantStatus = chatState?.assistant?.status ?? 'idle';
  const streamingContent = chatState?.assistant?.streamingContent ?? '';
  const firstTokenReceived = chatState?.assistant?.firstTokenReceived ?? false;
  const currentStep = chatState?.assistant?.currentStep ?? null;
  const error = chatState?.error ?? null;
  const sessionId = chatState?.sessionId ?? null;
  const currentSkill = chatState?.currentSkill ?? 'general-admin';
  const activeToolCalls = chatState?.activeToolCalls ?? emptyToolCalls;
  const markdownPayloads = chatState?.markdownPayloads ?? emptyPayloads;
  const activePayloadIndex = chatState?.activePayloadIndex ?? null;

  const activePayload =
    activePayloadIndex !== null ? markdownPayloads[activePayloadIndex] ?? null : null;

  // Trigger streaming via RTK Query - each new runId creates a new cache entry
  useStreamAgentChatQuery(
    activeStream
      ? {
          prompt: activeStream.prompt,
          sessionId,
          skill: currentSkill,
          runId: activeStream.runId,
        }
      : ({} as never),
    { skip: !activeStream },
  );

  // Corrected send handler
  const handleSend = useCallback(() => {
    if (!input.trim()) return;
    if (assistantStatus !== 'idle') return;

    const prompt = input.trim();
    dispatch(addUserMessage(prompt));
    setInput('');

    const runId = generateRunId();
    setActiveStream({ prompt, runId });
  }, [input, dispatch, assistantStatus]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewConversation = useCallback(() => {
    dispatch(newConversation());
    setActiveStream(null);
    inputRef.current?.focus();
  }, [dispatch]);

  const handleDismissError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  const handlePayloadClick = useCallback(
    (index: number) => {
      dispatch(setActivePayloadIndex(index));
    },
    [dispatch],
  );

  const handleClosePanel = useCallback(() => {
    dispatch(closeMarkdownPanel());
  }, [dispatch]);

  // Drag-to-resize handler for the markdown panel
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;
    const startX = e.clientX;
    const startWidth = panelWidth;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!isDraggingRef.current) return;
      const delta = startX - moveEvent.clientX;
      const containerWidth = containerRef.current?.offsetWidth ?? window.innerWidth;
      const maxWidth = containerWidth * 0.8;
      const newWidth = Math.max(MIN_PANEL_WIDTH, Math.min(startWidth + delta, maxWidth));
      setPanelWidth(newWidth);
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [panelWidth]);

  // Build display messages list, interleaving markdown payload chips chronologically
  const displayItems = useMemo(() => {
    const items: React.ReactNode[] = [];

    // Merge messages and payloads into chronological order
    let payloadIdx = 0;

    messages.forEach(msg => {
      const msgTime = new Date(msg.timestamp).getTime();

      // Insert any payload chips that arrived before this message
      while (payloadIdx < markdownPayloads.length && markdownPayloads[payloadIdx].timestamp <= msgTime) {
        const pl = markdownPayloads[payloadIdx];
        items.push(
          <MarkdownPayloadChip
            key={pl.id}
            payload={pl}
            index={payloadIdx}
            isActive={activePayloadIndex === payloadIdx}
            onClick={handlePayloadClick}
          />,
        );
        payloadIdx++;
      }

      items.push(
        <AgentMessageBubble
          key={msg.id}
          id={msg.id}
          role={msg.role}
          content={msg.content}
          isHovered={hoveredMessageId === msg.id}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />,
      );
    });

    // Insert any remaining payloads that arrived after all messages
    while (payloadIdx < markdownPayloads.length) {
      const pl = markdownPayloads[payloadIdx];
      items.push(
        <MarkdownPayloadChip
          key={pl.id}
          payload={pl}
          index={payloadIdx}
          isActive={activePayloadIndex === payloadIdx}
          onClick={handlePayloadClick}
        />,
      );
      payloadIdx++;
    }

    // Show active tool calls
    activeToolCalls.forEach(tc => {
      items.push(<ToolCallBubble key={`tool-${tc.toolCallId}`} toolCall={tc} />);
    });

    // Show activity snapshots
    Object.values(activities).forEach(activity => {
      items.push(
        <ThinkingBubble
          key={`activity-${activity.messageId}`}
          message={activity.message}
        />,
      );
    });

    // Show thinking indicator when no other indicators
    if (
      assistantStatus === 'thinking' &&
      activeToolCalls.length === 0 &&
      Object.keys(activities).length === 0
    ) {
      items.push(
        <ThinkingBubble key="thinking" message={currentStep || 'Thinking...'} />,
      );
    }

    // Show streaming content
    if (assistantStatus === 'streaming' && streamingContent && firstTokenReceived) {
      /* eslint-disable jsx-a11y/aria-role */
      items.push(
        <AgentMessageBubble
          key="streaming-temp"
          id="streaming-temp"
          role="assistant"
          content={streamingContent}
          isStreaming
        />,
      );
      /* eslint-enable jsx-a11y/aria-role */
    }

    return items;
  }, [
    messages,
    markdownPayloads,
    activePayloadIndex,
    handlePayloadClick,
    activeToolCalls,
    activities,
    assistantStatus,
    streamingContent,
    firstTokenReceived,
    currentStep,
    hoveredMessageId,
    handleMouseEnter,
    handleMouseLeave,
  ]);

  const isInputDisabled = assistantStatus !== 'idle';
  const hasMessages = messages.length > 0;

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Chat panel */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minWidth: 320,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AgentIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Admin Agent
            </Typography>
            {sessionId && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Session active
              </Typography>
            )}
          </Box>
          {hasMessages && (
            <Tooltip title="New conversation">
              <IconButton onClick={handleNewConversation} size="small">
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" onClose={handleDismissError} sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {/* Messages Container */}
        <ScrollContainer
          emptyState={
            <EmptyState
              icon={<AgentIcon sx={{ fontSize: 28, color: 'white' }} />}
              title="Admin Agent"
              description="Ask the agent to help with admin tasks like searching users, investigating issues, or generating reports."
              examples={[
                'Search for user Alfred Souza',
                'Show me the latest sign-ups this week',
                'Generate a dossier for user@example.com',
              ]}
              onExampleClick={(example: string) => {
                setInput(example);
                inputRef.current?.focus();
              }}
            />
          }
        >
          {displayItems}
        </ScrollContainer>

        {/* Input Footer */}
        <InputFooter elevation>
          <Stack direction="row" spacing={1} sx={{alignItems: "flex-end",  width: '100%' }}>
            <TextField
              inputRef={inputRef}
              fullWidth
              size="small"
              placeholder="Ask the admin agent..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyPress}
              disabled={isInputDisabled}
              multiline
              minRows={2}
              maxRows={6}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'white',
                  borderRadius: 2,
                  '& fieldset': { borderColor: '#d5d3e0' },
                  '&:hover fieldset': { borderColor: '#ada1f3' },
                  '&.Mui-focused fieldset': { borderColor: '#836eed', borderWidth: 2 },
                  '&.Mui-disabled': { bgcolor: '#faf9fc' },
                },
                '& .MuiInputBase-input': { fontSize: 14, lineHeight: 1.5 },
              }}
            />
            <IconButton
              onClick={handleSend}
              disabled={!input.trim() || isInputDisabled}
              sx={{
                bgcolor: '#4D32D7',
                color: 'white',
                opacity: !input.trim() || isInputDisabled ? 0.3 : 1,
                '&:hover': {
                  bgcolor: '#36229e',
                  opacity: !input.trim() || isInputDisabled ? 0.3 : 1,
                },
                '&:disabled': { bgcolor: '#4D32D7', color: 'white', opacity: 0.3 },
                transition: 'all 0.2s ease',
                width: 40,
                height: 40,
              }}
            >
              <SendIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </Stack>
        </InputFooter>
      </Box>

      {/* Markdown payload side panel */}
      {activePayload && (
        <MarkdownPanel
          payload={activePayload}
          panelWidth={panelWidth}
          onClose={handleClosePanel}
          onDragStart={handleDragStart}
        />
      )}
    </Box>
  );
}
